import { subscriptionRequiredMessage } from "../subscriptions/access";

export type RecordState = "active" | "archived";

export interface BillingAddress {
  line1: string | null;
  line2: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface ContactRecord {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  state: RecordState;
  version: number;
  billingAddress?: BillingAddress;
}

export interface ClientRecord {
  searchMatch?: import("../../shared/navigation-search").SearchMatch;
  id: string;
  displayName: string;
  invoicePrefix?: string | null;
  state: RecordState;
  version: number;
  primaryContact: ContactRecord | null;
  billingAddress: BillingAddress;
}

export class ClientRequestError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function clientRequest<T>(getToken: () => Promise<string | null>, path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  if (!token) throw new ClientRequestError(401, "Your session is unavailable. Sign in and try again.");
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const subscriptionMessage = await subscriptionRequiredMessage(response);
    if (subscriptionMessage) throw new ClientRequestError(response.status, subscriptionMessage);
    const message = response.status === 400
      ? "Check the highlighted information and try again."
      : response.status === 404
        ? "This client is unavailable."
        : response.status === 409
          ? "This record changed elsewhere. Refresh it before saving again."
          : "Clients are temporarily unavailable. Try again.";
    throw new ClientRequestError(response.status, message);
  }
  return response.json() as Promise<T>;
}
